import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useStaffAuth } from "../../context/StaffAuthContext";
import { useTheme } from "../../context/Themecontext";
import axios from "axios";
import {
  Menu,
  X,
  MessageSquare,
  LogOut,
  ChevronDown,
  Pencil,
  Building2,
  Sun,
  Moon,
} from "lucide-react";
import NotificationBell from "../notifications/NotificationBell";

const API = process.env.REACT_APP_API_URL || "http://localhost:5000";
const api = axios.create({ baseURL: API, withCredentials: true });

function useClickOutside(ref, onOutside) {
  useEffect(() => {
    function handle(e) {
      if (ref.current && !ref.current.contains(e.target)) onOutside();
    }
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, [ref, onOutside]);
}

function getInitials(name) {
  if (!name || typeof name !== "string") return "U";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "U";
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

/** Reject "", "null", and non-URL strings that break <img> for staff */
function resolveAvatarUrl(value) {
  if (value == null) return null;
  const s = String(value).trim();
  if (!s) return null;
  if (/^(null|undefined|none|n\/a)$/i.test(s)) return null;
  if (/^https?:\/\//i.test(s)) return s;
  if (s.startsWith("/")) return s;
  if (s.startsWith("data:image")) return s;
  return null;
}

function LetterAvatar({ initials, size = "md" }) {
  const sizeCls =
    size === "lg"
      ? "h-10 w-10 text-sm"
      : "h-7 w-7 sm:h-8 sm:w-8 text-[11px] sm:text-xs";
  return (
    <span
      className={`flex items-center justify-center rounded-full bg-gradient-to-br from-primary-500 to-primary-800 font-bold text-white ${sizeCls}`}
    >
      {initials}
    </span>
  );
}

function ProfileAvatar({ src, name, size = "md" }) {
  const [broken, setBroken] = useState(false);
  const initials = getInitials(name);
  const url = resolveAvatarUrl(src);

  if (!url || broken) {
    return <LetterAvatar initials={initials} size={size} />;
  }

  const imgCls =
    size === "lg"
      ? "h-10 w-10 rounded-full object-cover ring-1 ring-neutral-200 dark:ring-neutral-700"
      : "h-7 w-7 sm:h-8 sm:w-8 rounded-full object-cover ring-1 ring-neutral-200 dark:ring-neutral-700";

  return (
    <img
      src={url}
      alt={name || "User"}
      className={imgCls}
      onError={() => setBroken(true)}
    />
  );
}

/**
 * School logo with graceful fallback — mirrors Sidebar LogoImage.
 */
function SchoolLogoImage({ src, alt, broken, onError }) {
  if (!src || broken) {
    return (
      <span className="flex h-9 w-9 sm:h-10 sm:w-10 shrink-0 items-center justify-center rounded-md border border-neutral-200 dark:border-neutral-800 bg-neutral-100 text-neutral-400 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-500">
        <Building2 className="h-4 w-4" strokeWidth={2} />
      </span>
    );
  }
  return (
    <img
      src={src}
      alt={alt || "School logo"}
      onError={onError}
      className="h-9 w-9 sm:h-10 sm:w-10 shrink-0 rounded-md object-contain bg-transparent"
    />
  );
}

function normalizeProfile(src) {
  if (!src || typeof src !== "object") return null;
  const name =
    src.name ||
    src.fullName ||
    [src.firstName, src.middleName, src.lastName].filter(Boolean).join(" ") ||
    src.email ||
    src.staffCode ||
    "";
  if (!name && !src.email) return null;

  const roleObj = src.roleId && typeof src.roleId === "object" ? src.roleId : null;

  return {
    name: name || "User",
    email: src.email || "",
    role:
      src.role ||
      src.roleName ||
      src.designation ||
      roleObj?.roleName ||
      "",
    activeBatch:
      src.activeBatch ||
      src.academicYear ||
      src.currentAcademicYear ||
      src.batchName ||
      src.activeAcademicYearName ||
      "",
    profileImage: resolveAvatarUrl(src.profileImage || src.avatar),
    schoolName: src.schoolName || src.school?.name || "",
  };
}

/**
 * Same panel detection as Sidebar so header/sidebar never drift.
 */
function detectPanelMode(isPortalProp) {
  if (typeof window === "undefined") {
    return isPortalProp ? "staff" : "owner";
  }
  const path = window.location.pathname || "";
  if (
    path.startsWith("/student-panel") ||
    path.startsWith("/student-home") ||
    path.startsWith("/student/")
  ) {
    return "student";
  }
  if (
    isPortalProp ||
    path.startsWith("/staff-panel") ||
    path.startsWith("/staff-home") ||
    path.startsWith("/staff/")
  ) {
    return "staff";
  }
  return "owner";
}

/**
 * Same endpoints Sidebar uses:
 *   Owner:   GET /school/me
 *   Staff:   GET /staff-portal/branding (fallback /school-portal/branding)
 *   Student: GET /student-dashboard/branding
 */
async function fetchSchoolBranding(panelMode) {
  if (panelMode === "staff") {
    let d = null;
    try {
      const res = await api.get("/staff-portal/branding");
      d = res.data || {};
    } catch (err) {
      if (err?.response?.status === 404) {
        const res = await api.get("/school-portal/branding");
        d = res.data || {};
      } else {
        throw err;
      }
    }
    return {
      name: d.schoolName || "",
      logoUrl: d.logo?.url || null,
    };
  }
  if (panelMode === "student") {
    const res = await api.get("/student-dashboard/branding");
    const d = res.data || {};
    return {
      name: d.schoolName || "",
      logoUrl: d.logo?.url || null,
    };
  }
  const res = await api.get("/school/me");
  const school = res.data || {};
  return {
    name: school.schoolName || "",
    logoUrl: school.logo?.url || null,
  };
}

export default function DashboardHeader({
  onMenuToggle,
  isMobileMenuOpen,
  userProfile,
  user,
  onLogout,
  logout,
  onChatClick,
  chatUnreadCount = 0,
  onEditProfile,
  editProfilePath,
  notificationsPath,
  chatPath,
  isPortal = false,
  /** Optional override — skip fetch when parent already has school data */
  tenant: tenantProp,
}) {
  const navigate = useNavigate();
  const { isDark, toggleTheme } = useTheme();

  const {
    staff: ctxStaff,
    user: ctxUser,
    currentUser: ctxCurrentUser,
    logout: ctxLogout,
    handleLogout: ctxHandleLogout,
  } = useStaffAuth() || {};

  const [fetchedProfile, setFetchedProfile] = useState(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const profileRef = useRef(null);

  useClickOutside(profileRef, () => setProfileOpen(false));

  const panelMode = detectPanelMode(isPortal);
  const portalMode = panelMode === "staff" || panelMode === "student";

  // ---------- School branding (visible when sidebar is hidden: < lg / ≤1023px) ----------
  // Requirement: logo must show at viewport ≤768px. Sidebar is drawer-only below lg
  // (1024px), so header branding uses `lg:hidden` (= visible at all widths < 1024px,
  // which includes ≤768px).
  const [tenant, setTenant] = useState({
    name: tenantProp?.name || tenantProp?.schoolName || "",
    logoUrl: tenantProp?.logoUrl || tenantProp?.logo?.url || null,
  });
  const [logoBroken, setLogoBroken] = useState(false);

  useEffect(() => {
    setLogoBroken(false);
  }, [tenant.logoUrl]);

  useEffect(() => {
    if (
      tenantProp?.logoUrl ||
      tenantProp?.logo?.url ||
      tenantProp?.name ||
      tenantProp?.schoolName
    ) {
      setTenant({
        name: tenantProp.name || tenantProp.schoolName || "",
        logoUrl: tenantProp.logoUrl || tenantProp.logo?.url || null,
      });
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const branding = await fetchSchoolBranding(panelMode);
        if (!cancelled) setTenant(branding);
      } catch (err) {
        console.warn(
          "Header branding fetch failed:",
          panelMode,
          err?.response?.status || err?.message
        );
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [tenantProp, panelMode]);
  // -------------------------------------------------------------------

  useEffect(() => {
    const fromProps = normalizeProfile(userProfile || user);
    if (fromProps) {
      setFetchedProfile(fromProps);
      return;
    }

    const fromCtx = normalizeProfile(ctxStaff || ctxUser || ctxCurrentUser);
    if (fromCtx) {
      setFetchedProfile(fromCtx);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        if (panelMode === "staff") {
          const res = await api.get("/staff-auth/me");
          const staff = res.data?.staff || res.data;
          const n = normalizeProfile(staff);
          if (!cancelled && n) setFetchedProfile(n);
        } else if (panelMode === "student") {
          try {
            const res = await api.get("/student-auth/me");
            const s = res.data?.student || res.data;
            const n = normalizeProfile(s);
            if (!cancelled && n) setFetchedProfile(n);
          } catch (_) {
            /* ignore */
          }
        } else {
          try {
            const res = await api.get("/auth/me");
            const u = res.data?.user || res.data;
            const n = normalizeProfile(u);
            if (!cancelled && n) setFetchedProfile(n);
          } catch (_) {
            /* ignore */
          }
        }
      } catch (err) {
        console.warn("Header profile fetch failed:", err?.message);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProfile, user, panelMode, ctxStaff, ctxUser, ctxCurrentUser]);

  const profile = useMemo(
    () =>
      fetchedProfile || {
        name: "User",
        email: "",
        role: "",
        activeBatch: "",
        profileImage: null,
        schoolName: "",
      },
    [fetchedProfile]
  );

  const resolvedChatPath =
    chatPath ||
    (panelMode === "staff"
      ? "/staff-panel/chat"
      : panelMode === "student"
        ? "/student-panel/chat"
        : "/chat");
  const resolvedNotifPath =
    notificationsPath ||
    (panelMode === "staff"
      ? "/staff-panel/notifications"
      : panelMode === "student"
        ? "/student-panel/notifications"
        : "/notifications");
  const resolvedEditPath =
    editProfilePath ||
    (panelMode === "staff"
      ? "/staff-panel/my-profile"
      : panelMode === "student"
        ? "/student-panel/my-profile"
        : "/my-profile");

  const handleChat = () => {
    if (onChatClick) {
      onChatClick();
      return;
    }
    navigate(resolvedChatPath);
  };

  const handleEditProfile = () => {
    setProfileOpen(false);
    if (onEditProfile) {
      onEditProfile();
      return;
    }
    navigate(resolvedEditPath);
  };

  const handleLogout = useCallback(async () => {
    if (loggingOut) return;
    setLoggingOut(true);
    setProfileOpen(false);

    const parentLogout =
      onLogout || logout || ctxLogout || ctxHandleLogout;

    try {
      if (typeof parentLogout === "function") {
        await parentLogout();
      } else if (panelMode === "staff") {
        await api.post("/staff-auth/logout");
      } else if (panelMode === "student") {
        await api.post("/student-auth/logout");
      } else {
        try {
          await api.post("/auth/logout");
        } catch (_) {
          await api.post("/staff-auth/logout");
        }
      }
    } catch (err) {
      console.warn("Logout API error:", err?.message);
    } finally {
      setLoggingOut(false);
      window.location.href =
        panelMode === "staff"
          ? "/staff-login"
          : panelMode === "student"
            ? "/student-login"
            : "/login";
    }
  }, [loggingOut, onLogout, logout, ctxLogout, ctxHandleLogout, panelMode]);

  return (
    // Solid header background (no glassmorphism/blur): plain white in
    // light mode, plain dark neutral in dark mode — tracks the theme
    // via `dark:` instead of a translucent/backdrop-blur treatment.
    <header className="sticky top-0 z-40 w-full border-b border-neutral-200 bg-white font-sans dark:border-neutral-800 dark:bg-neutral-900">
      <div className="mx-auto flex h-14 sm:h-16 items-center gap-2 px-3 sm:px-6">
        <button
          type="button"
          onClick={onMenuToggle}
          className="inline-flex h-8 w-8 sm:h-9 sm:w-9 items-center justify-center rounded text-neutral-600 hover:bg-neutral-100 hover:text-primary-700 dark:text-neutral-300 dark:hover:bg-neutral-800 dark:hover:text-primary-400 lg:hidden"
          aria-label="Toggle menu"
          aria-expanded={isMobileMenuOpen}
        >
          {isMobileMenuOpen ? <X size={18} /> : <Menu size={18} />}
        </button>

        {/*
          School branding — shown whenever the desktop sidebar is hidden.
          Sidebar is visible from `lg` (1024px) up, so this block uses
          `lg:hidden` → visible at every width < 1024px, which includes
          all viewports ≤ 768px as required.
        */}
        <div className="flex min-w-0 flex-1 items-center gap-2 lg:hidden">
          <SchoolLogoImage
            src={tenant.logoUrl}
            alt={tenant.name || "School"}
            broken={logoBroken}
            onError={() => setLogoBroken(true)}
          />
          {(tenant.name || profile.schoolName) && (
            <span className="min-w-0 truncate text-sm font-semibold text-neutral-900 dark:text-neutral-100 sm:text-base">
              {tenant.name || profile.schoolName}
            </span>
          )}
        </div>

        {/* Spacer only needed on desktop where branding is in the sidebar */}
        <div className="hidden flex-1 lg:block" />

        <div className="flex shrink-0 items-center gap-1 sm:gap-2">
          <button
            type="button"
            onClick={toggleTheme}
            className="inline-flex h-8 w-8 sm:h-9 sm:w-9 items-center justify-center rounded text-neutral-600 hover:bg-neutral-100 hover:text-primary-700 dark:text-neutral-300 dark:hover:bg-neutral-800 dark:hover:text-primary-400"
            aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
            title={isDark ? "Switch to light mode" : "Switch to dark mode"}
          >
            {isDark ? <Sun size={18} /> : <Moon size={18} />}
          </button>

          <button
            type="button"
            onClick={handleChat}
            className="relative inline-flex h-8 w-8 sm:h-9 sm:w-9 items-center justify-center rounded text-neutral-600 hover:bg-neutral-100 hover:text-primary-700 dark:text-neutral-300 dark:hover:bg-neutral-800 dark:hover:text-primary-400"
            aria-label="Chat"
          >
            <MessageSquare size={18} />
            {chatUnreadCount > 0 && (
              <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-accent-500 px-1 text-[9px] font-semibold leading-none text-white ring-2 ring-white dark:ring-neutral-900">
                {chatUnreadCount}
              </span>
            )}
          </button>

          <NotificationBell
            notificationsPath={resolvedNotifPath}
            isPortal={portalMode}
          />

          <div className="relative ml-0.5 sm:ml-1" ref={profileRef}>
            <button
              type="button"
              onClick={() => setProfileOpen((v) => !v)}
              className="flex items-center gap-1.5 rounded-full pl-0.5 pr-1 py-0.5 hover:bg-neutral-100 dark:hover:bg-neutral-800 sm:gap-2 sm:rounded sm:pl-1 sm:pr-2"
              aria-label="Account menu"
              aria-expanded={profileOpen}
            >
              <span className="relative shrink-0">
                <ProfileAvatar
                  src={profile.profileImage}
                  name={profile.name}
                  size="md"
                />
                <span className="absolute -bottom-0.5 -right-0.5 h-2 w-2 sm:h-2.5 sm:w-2.5 rounded-full border-2 border-white bg-success-500 dark:border-neutral-900" />
              </span>
              <span className="hidden max-w-[120px] truncate text-xs font-medium text-neutral-700 dark:text-neutral-200 sm:block">
                {profile.name}
              </span>
              <ChevronDown
                size={14}
                className={`hidden text-neutral-400 dark:text-neutral-500 transition-transform duration-150 sm:block ${
                  profileOpen ? "rotate-180" : ""
                }`}
              />
            </button>

            {profileOpen && (
              <div className="absolute right-0 z-50 mt-2 w-64 max-w-[90vw] overflow-hidden rounded-card border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 shadow-popover dark:border-neutral-700 dark:bg-neutral-800 dark:shadow-black/40">
                <div className="border-b border-neutral-100 dark:border-neutral-800 px-4 py-3 dark:border-neutral-700">
                  <div className="flex items-center gap-3">
                    <ProfileAvatar
                      src={profile.profileImage}
                      name={profile.name}
                      size="lg"
                    />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-xs sm:text-body-sm font-semibold text-neutral-800 dark:text-neutral-100">
                        {profile.name}
                      </p>
                      {profile.email ? (
                        <p className="truncate text-[10px] sm:text-label-sm text-neutral-400 dark:text-neutral-500">
                          {profile.email}
                        </p>
                      ) : null}
                      {profile.role ? (
                        <p className="truncate text-[10px] sm:text-label-sm text-primary-600 dark:text-primary-400">
                          {profile.role}
                        </p>
                      ) : null}
                    </div>
                  </div>
                  {(profile.activeBatch || profile.schoolName || tenant.name) && (
                    <div className="mt-2.5 space-y-1 rounded-md bg-neutral-50 dark:bg-neutral-950 px-2.5 py-1.5 dark:bg-neutral-900/60">
                      {(tenant.name || profile.schoolName) && (
                        <p className="truncate text-[10px] text-neutral-500 dark:text-neutral-400">
                          {tenant.name || profile.schoolName}
                        </p>
                      )}
                      {profile.activeBatch ? (
                        <p className="truncate text-[10px] sm:text-label-sm font-medium text-neutral-700 dark:text-neutral-200">
                          Active batch:{" "}
                          <span className="text-primary-700 dark:text-primary-400">
                            {profile.activeBatch}
                          </span>
                        </p>
                      ) : null}
                    </div>
                  )}
                </div>

                <button
                  type="button"
                  onClick={handleEditProfile}
                  className="group flex w-full items-center gap-2.5 px-4 py-2.5 text-left text-xs sm:text-body-sm font-medium text-neutral-600 transition-colors hover:bg-neutral-50 dark:hover:bg-neutral-800 hover:text-primary-700 dark:text-neutral-300 dark:hover:bg-neutral-700/60 dark:hover:text-primary-400"
                >
                  <Pencil className="h-3.5 w-3.5 sm:h-4 sm:w-4 shrink-0 text-neutral-400 group-hover:text-primary-600 dark:text-neutral-500 dark:group-hover:text-primary-400" />
                  Edit profile
                </button>

                <button
                  type="button"
                  onClick={handleLogout}
                  disabled={loggingOut}
                  className="group flex w-full items-center gap-2.5 border-t border-neutral-100 dark:border-neutral-800 px-4 py-2.5 text-left text-xs sm:text-body-sm font-medium text-neutral-500 transition-colors hover:bg-danger-50 hover:text-danger-700 disabled:opacity-60 dark:border-neutral-700 dark:text-neutral-400 dark:hover:bg-danger-500/10 dark:hover:text-danger-500"
                >
                  <LogOut className="h-3.5 w-3.5 sm:h-4 sm:w-4 shrink-0 text-neutral-400 group-hover:text-danger-500 dark:text-neutral-500" />
                  {loggingOut ? "Logging out…" : "Log out"}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}